import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/badge.default.js';
import * as wireframeSkin from './skins/badge.wireframe.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

const VALID_VARIANTS = ['neutral', 'info', 'success', 'warning', 'error'];
const VALID_SIZES = ['sm', 'md', 'lg'];

class RunBadge extends RunElement {
  #variant = 'neutral';
  #size = 'md';
  #pill = false;
  #dot = false;
  #rendering = false;

  static get observedAttributes() {
    return ['variant', 'size', 'pill', 'dot'];
  }

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'status');
    this.#updateAria();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'variant': this.variant = newValue; break;
      case 'size':    this.size = newValue; break;
      case 'pill':    this.pill = newValue !== null; break;
      case 'dot':     this.dot = newValue !== null; break;
    }
  }

  get variant() { return this.#variant; }
  set variant(value) {
    const coerced = VALID_VARIANTS.includes(String(value).trim())
      ? String(value).trim()
      : 'neutral';
    if (this.#variant === coerced) return;
    this.#variant = coerced;
    this.setAttribute('variant', coerced);
    this.#updateAria();
    this.requestUpdate();
  }

  get size() { return this.#size; }
  set size(value) {
    const coerced = VALID_SIZES.includes(String(value).trim())
      ? String(value).trim()
      : 'md';
    if (this.#size === coerced) return;
    this.#size = coerced;
    this.setAttribute('size', coerced);
    this.requestUpdate();
  }

  get pill() { return this.#pill; }
  set pill(value) {
    const coerced = Boolean(value);
    if (this.#pill === coerced) return;
    this.#pill = coerced;
    this.toggleAttribute('pill', coerced);
    this.requestUpdate();
  }

  get dot() { return this.#dot; }
  set dot(value) {
    const coerced = Boolean(value);
    if (this.#dot === coerced) return;
    this.#dot = coerced;
    this.toggleAttribute('dot', coerced);
    this.#updateAria();
    this.requestUpdate();
  }

  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
    } finally {
      this.#rendering = false;
    }
    this.#updateAria();
  }

  #resolveSkin() {
    const s = this.getAttribute('skin');
    if (s) return s;
    return resolveSkinFromAncestors(this);
  }

  #updateAria() {
    if (this.#dot) {
      this.setAttribute('aria-label', this.#variant);
    } else {
      this.removeAttribute('aria-label');
    }
  }
}

if (!customElements.get('run-badge')) {
  customElements.define('run-badge', RunBadge);
}

export { RunBadge };
export default RunBadge;